import {KeyIcon} from "@heroicons/react/20/solid";
import {extractErrorMessage} from "@ots-frontend/ots-data";
import {ChangeEvent, useEffect, useMemo, useRef, useState} from "react";
import {OtsFormDialog} from "../components/OtsFormDialog";
import {OtsPasswordInput} from "../components/OtsPasswordInput";
import {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {
  ChangePasswordErrors,
  ChangePasswordField,
  ChangePasswordValues,
  defaultPasswordPolicy,
  evaluatePasswordRules,
  isChangePasswordFieldError,
  PasswordPolicy,
  validateChangePassword
} from "./password-policy";
import {PasswordRuleChecklist, PasswordStrengthMeter} from "./PasswordStrengthMeter";

export type ChangePasswordRequest = {
  /** Absent for an administrator's reset, which the server authorizes by role instead. */
  currentPassword?: string;
  newPassword: string;
};

export type ChangePasswordDialogProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Sends the change. Reject to report a failure: throw a `ChangePasswordFieldError` to pin it to a
   * field (e.g. a wrong current password), or anything else to show it above the buttons.
   */
  onSubmit: (request: ChangePasswordRequest) => Promise<void> | void;
  onSuccess?: () => void;
  /** False for an administrator resetting someone else's password. Defaults to true. */
  requireCurrentPassword?: boolean;
  /** Whose password is being reset, when it is not the viewer's own. */
  subjectName?: string;
  /** Lets password managers file the new password under the right account, and is disallowed as the password. */
  userName?: string;
  policy?: PasswordPolicy;
  brand?: OtsBrand;
  /**
   * Light or dark. Defaults to "system" -- the host page's theme, else the OS -- for a dialog opened
   * on its own. A page opening it from inside its own theme root passes "inherit" to match itself.
   */
  colorScheme?: OtsColorSchemePreference;
};

const emptyValues: ChangePasswordValues = {currentPassword: "", newPassword: "", confirmPassword: ""};
const fieldOrder: ChangePasswordField[] = ["currentPassword", "newPassword", "confirmPassword"];

/** Change your own password, or reset someone else's when `requireCurrentPassword` is false. */
export const ChangePasswordDialog = ({
  open,
  onClose,
  onSubmit,
  onSuccess,
  requireCurrentPassword = true,
  subjectName,
  userName,
  policy = defaultPasswordPolicy,
  brand,
  colorScheme
}: ChangePasswordDialogProps) => {
  const [values, setValues] = useState<ChangePasswordValues>(emptyValues);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ChangePasswordErrors>({});
  const [formError, setFormError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const inputs = useRef<Partial<Record<ChangePasswordField, HTMLInputElement | null>>>({});

  // Every opening starts clean; nothing typed last time is carried into the next attempt.
  useEffect(() => {
    if (open) {
      setValues(emptyValues);
      setAttempted(false);
      setServerErrors({});
      setFormError(undefined);
      setSucceeded(false);
    }
  }, [open]);

  const validationErrors = useMemo(
    () => validateChangePassword(values, {policy, requireCurrentPassword, userName}),
    [values, policy, requireCurrentPassword, userName]
  );

  // Nothing is flagged before the first submit: an empty form is not a mistake yet. After that the
  // errors track what is typed, so fixing a field clears its message straight away.
  const errors: ChangePasswordErrors = {...(attempted ? validationErrors : {}), ...serverErrors};

  const rules = useMemo(
    () => evaluatePasswordRules(values.newPassword, policy, {
      currentPassword: requireCurrentPassword ? values.currentPassword : undefined,
      userName
    }),
    [values.newPassword, values.currentPassword, policy, requireCurrentPassword, userName]
  );

  const onFieldChange = (field: ChangePasswordField) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValues((previous) => ({...previous, [field]: value}));
    setServerErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const next = {...previous};
      delete next[field];
      return next;
    });
    setFormError(undefined);
  };

  const focusField = (field?: ChangePasswordField) => {
    if (field) {
      inputs.current[field]?.focus();
    }
  };

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    setAttempted(true);
    setFormError(undefined);

    if (Object.keys(validationErrors).length > 0) {
      focusField(fieldOrder.find((field) => validationErrors[field]));
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        currentPassword: requireCurrentPassword ? values.currentPassword : undefined,
        newPassword: values.newPassword
      });
      setValues(emptyValues);
      setSucceeded(true);
      onSuccess?.();
    } catch (error) {
      if (isChangePasswordFieldError(error)) {
        setServerErrors({[error.field]: error.message});
        focusField(error.field);
      } else {
        setFormError(extractErrorMessage(error, "The password couldn't be changed. Try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isReset = !requireCurrentPassword;
  const who = subjectName ?? "They";

  return (
    <OtsFormDialog
      open={open}
      onClose={onClose}
      onSubmit={() => void handleSubmit()}
      title={isReset ? (subjectName ? `Reset password for ${subjectName}` : "Reset password") : "Change password"}
      description={isReset
        ? "Set a new password and share it with them securely."
        : "Enter your current password, then choose a new one."}
      icon={<KeyIcon/>}
      formLabel={isReset ? "Reset password" : "Change password"}
      error={formError}
      submitLabel={isReset ? "Reset password" : "Update password"}
      submittingLabel="Updating…"
      progressLabel="Updating password"
      submitting={submitting}
      completion={succeeded ? {
        title: "Password updated",
        description: isReset
          ? `${who} will need the new password the next time they sign in.`
          : "Use your new password the next time you sign in."
      } : undefined}
      brand={brand}
      colorScheme={colorScheme}>
      {/* Lets password managers save the new password against the right account. */}
      {userName && (
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={userName}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"/>
      )}

      <div className="mt-2 space-y-5">
        {requireCurrentPassword && (
          <OtsPasswordInput
            ref={(element) => { inputs.current.currentPassword = element; }}
            label="Current password"
            name="currentPassword"
            autoComplete="current-password"
            autoFocus
            value={values.currentPassword}
            onChange={onFieldChange("currentPassword")}
            readOnly={submitting}
            error={errors.currentPassword}/>
        )}

        <OtsPasswordInput
          ref={(element) => { inputs.current.newPassword = element; }}
          label="New password"
          name="newPassword"
          autoComplete="new-password"
          autoFocus={!requireCurrentPassword}
          value={values.newPassword}
          onChange={onFieldChange("newPassword")}
          readOnly={submitting}
          error={errors.newPassword}
          footer={(
            <div className="mt-3 space-y-3">
              <PasswordStrengthMeter password={values.newPassword} minLength={policy.minLength}/>
              <PasswordRuleChecklist rules={rules}/>
            </div>
          )}/>

        <OtsPasswordInput
          ref={(element) => { inputs.current.confirmPassword = element; }}
          label="Confirm new password"
          name="confirmPassword"
          autoComplete="new-password"
          value={values.confirmPassword}
          onChange={onFieldChange("confirmPassword")}
          readOnly={submitting}
          error={errors.confirmPassword}/>
      </div>
    </OtsFormDialog>
  );
};
